import type { Editor } from '@tiptap/core';

import type { CellData } from '../../../../common/table/TableTypes';
import getTiptapNodeTextContent from '../../../../common/tiptap/utils/jsonContentUtils/getTiptapNodeTextContent';
import type { TiptapContent } from '../../../../common/tiptap/tiptapTypes';
import type { MilanoteEditorRef } from '../../../components/editor/milanoteEditor/MilanoteEditorTypes';

import MilanoteCellEditor, { type InsertPlainTextAtSelectionOptions } from './MilanoteCellEditor';
import { Range } from '@tiptap/core';

export type TiptapCellEditorContent = TiptapContent | string | null;
type MilanoteEditorRefTiptap = MilanoteEditorRef<TiptapCellEditorContent>;

/**
 * Returns content that should replace the stored cell content when editing starts.
 */
const getCellOverwriteContent = ({
    initialValue,
    event,
}: {
    initialValue?: string;
    event?: Event;
}): string | undefined => {
    if (initialValue !== '') return initialValue;
    if (!(event instanceof KeyboardEvent)) return initialValue;
    if (event.isComposing || event.ctrlKey || event.metaKey || event.altKey) return initialValue;
    if (event.key.length !== 1) return initialValue;

    // When starting editing with a typed character (ie not backspace, enter, etc), replace the cell content with the typed character.
    return event.key;
};

/**
 * Handsontable editor shell for V3 table cells.
 */
export default class TiptapCellEditor extends MilanoteCellEditor<TiptapCellEditorContent, TiptapCellEditorContent> {
    beginEditing(initialValue?: string, event?: Event): void {
        const overwriteContent = getCellOverwriteContent({ initialValue, event });
        super.beginEditing(overwriteContent, event);
    }

    getEditorInstance(): MilanoteEditorRefTiptap | null {
        const { cellEditorRef } = this.milanoteHot.milanoteProps;
        return (cellEditorRef?.current as unknown as MilanoteEditorRefTiptap | null) || null;
    }

    onEditorBeginEditing(overwriteContent?: string): void {
        this.content = overwriteContent ?? this.getCellPlainTextContent();
    }

    onEditorChange(content: TiptapCellEditorContent): void {
        this.content = content;
    }

    getEditorValue(): string {
        if (!this.content) return '';
        if (typeof this.content === 'string') return this.content;

        return getTiptapNodeTextContent(this.content);
    }

    onEditorFinishEditing(): void {
        const cellEditor = this.getEditorInstance();
        if (!cellEditor) return;

        this.onEditorChange(cellEditor.getEditorState());
    }

    clearEditorContent(): void {
        this.content = null;
    }

    setPlainTextContent(plainText: string, style?: string[]): void {
        const cellEditor = this.getEditorInstance();
        if (!cellEditor) {
            this.content = plainText;
            return;
        }

        cellEditor.setPlainText(plainText, style);
        this.onEditorChange(cellEditor.getEditorState() || plainText);
    }

    getPlainTextContent(): string {
        const content = this.getEditorState();
        if (!content) return '';
        if (typeof content === 'string') return content;

        return getTiptapNodeTextContent(content);
    }

    /**
     * Inserts plain text at the active selection and can also replace characters immediately before it.
     */
    insertPlainTextAtSelection(plainText: string, options?: InsertPlainTextAtSelectionOptions): void {
        const cellEditor = this.getEditorInstance();
        const editor = cellEditor?.getEditor() as Editor | undefined;
        if (!editor) return;

        const replacePrevCharacters = Math.max(options?.replacePrevCharacters || 0, 0);
        const { from, to } = editor.state.selection;
        const replacementStart = Math.max(from - replacePrevCharacters, 1);

        editor.chain().focus().insertContentAt({ from: replacementStart, to }, plainText).run();

        this.onEditorChange(cellEditor?.getEditorState() || plainText);
    }

    getPlainTextSelection(): Range | null {
        const editor = this.getEditorInstance()?.getEditor() as Editor | undefined;
        if (!editor) return null;

        const { from, to } = editor.state.selection;

        return {
            from: Math.max(from - 1, 0),
            to: Math.max(to - 1, 0),
        };
    }

    focus(): void {
        const editor = this.getEditorInstance()?.getEditor() as { commands?: { focus?: () => void } } | undefined;
        editor?.commands?.focus?.();
    }

    hasValidElement(): boolean {
        return !!this.getEditorInstance();
    }

    setEditorState(content: TiptapCellEditorContent): void {
        this.content = content;

        const cellEditor = this.getEditorInstance();
        cellEditor?.onChange(content);
    }

    getEditorState(): TiptapCellEditorContent {
        const cellEditor = this.getEditorInstance();
        return cellEditor?.getEditorState() || this.content;
    }

    private getCellPlainTextContent(): string {
        const cellData = (this.cellProperties as { cellData?: CellData } | undefined)?.cellData;
        if (cellData?.tiptapContent) return getTiptapNodeTextContent(cellData.tiptapContent);

        return cellData?.value || '';
    }
}
